import React, { useEffect, useMemo } from "react";
import { Col, Row, FormGroup, Label, Input, FormText } from "reactstrap";
import { useTranslation, Trans } from "react-i18next";

import ReleaseStoreSection from "../../components/releaseStoreSection";

const AVAILABILITY_TIP_REASON_CODES = new Set([
  "dance_and_beatport_approval_required",
  "dance_and_traxsource_approval_required",
]);

const REASON_CODE_I18N_KEYS = {
  youtube_declaration_required: "releases.store_picker.message.youtube_declaration",
  dance_and_beatport_approval_required: "releases.store_picker.message.beatport",
  dance_and_traxsource_approval_required: "releases.store_picker.message.traxsource",
  dance_genre_required: "releases.store_picker.message.juno",
  paid_plan_required: "releases.store_picker.message.contact_support",
};

const noop = () => {};
const normalizeStoreId = (storeId) => (
  storeId === null || storeId === undefined ? null : String(storeId)
);
const toNormalizedIdSet = (values = [], getId = (value) => value) => new Set(
  values.map((value) => normalizeStoreId(getId(value))).filter((id) => id !== null),
);
const extractStoreId = (store) => {
  if (store === null || store === undefined) {
    return null;
  }

  if (typeof store !== "object") {
    return normalizeStoreId(store);
  }

  if (store.store && typeof store.store === "object") {
    return normalizeStoreId(store.store.id);
  }

  if (store.ddex_store && typeof store.ddex_store === "object") {
    return normalizeStoreId(store.ddex_store.id);
  }

  return normalizeStoreId(
    store.store_id ??
    store.store ??
    store.ddex_store_id ??
    store.ddex_store ??
    store.id
  );
};

const getStoreLabel = (store) => store.display_name || store.name || "";

const getUniqueStores = (stores) => {
  const seen = new Set();

  return stores.filter((store) => {
    const storeId = normalizeStoreId(store?.id);

    if (!store || storeId === null || seen.has(storeId)) {
      return false;
    }

    seen.add(storeId);
    return true;
  });
};

const expandStoreEntries = (stores) =>
  stores.flatMap((store) => {
    const labels = getStoreLabel(store).split(",").map((label) => label.trim()).filter(Boolean);

    return labels.map((label, index) => ({
      ...store,
      checkboxId: `${store.id}-${index}`,
      checkboxLabel: label,
    }));
  });

export const prepareDdexStores = (stores = []) =>
  getUniqueStores(
    stores
      .filter((store) => getStoreLabel(store))
      .sort((a, b) => getStoreLabel(a).localeCompare(getStoreLabel(b)))
  );

export const buildDdexStoreParams = ({
  labelId,
  genreId,
  youtubeDeclarationE,
  youtubeDeclarationNE,
  releaseId,
} = {}) => {
  const params = {};
  if (labelId !== null && labelId !== undefined && labelId !== "") {
    params.label = labelId;
  }
  if (genreId !== null && genreId !== undefined && genreId !== "") {
    params.genre = genreId;
  }
  if (youtubeDeclarationE) {
    params.youtube_declaration = true;
  } else if (youtubeDeclarationNE) {
    params.youtube_declaration = false;
  }
  if (releaseId !== null && releaseId !== undefined && releaseId !== "") {
    params.release = releaseId;
  }
  return params;
};

export const parseDdexStoreResponse = (data = {}) => ({
  stores: prepareDdexStores(data.stores || data.results || []),
});

const getStoreDisabledReason = (store, t, youtubeDeclarationE) => {
  if (store.available !== false) {
    return null;
  }

  if (store.reason_code === "youtube_declaration_conflict") {
    return youtubeDeclarationE
      ? t("releases.store_picker.message.youtube_content_id")
      : t("releases.store_picker.message.youtube_ineligible");
  }

  const i18nKey = REASON_CODE_I18N_KEYS[store.reason_code];
  if (i18nKey) {
    return t(i18nKey);
  }

  return store.default_message || null;
};

export const getSelectedStoreIds = (selectedOptionalStores = [], selectedMandatoryStores = [], selectedOptionalPaidStores = []) =>
  Array.from(
    new Set([...selectedOptionalStores, ...selectedMandatoryStores, ...selectedOptionalPaidStores].map((store) => store?.id))
  ).filter((storeId) => storeId !== null && storeId !== undefined);

export const getStoreIdsFromSource = (stores = []) =>
  stores
    .map((store) => extractStoreId(store))
    .filter((storeId) => storeId !== null && storeId !== undefined);

export const useReleaseStoresView = ({
  ddexStores,
  selectedStoreIds,
  storeSearchTerm = "",
}) => {
  const selectedStoreIdSet = useMemo(() => toNormalizedIdSet(selectedStoreIds), [selectedStoreIds]);
  const storeSearchMatcher = storeSearchTerm.trim().toLowerCase();

  const selectedMandatoryStores = useMemo(() => ddexStores.filter((store) =>
    store.category === "mandatory" && selectedStoreIdSet.has(normalizeStoreId(store.id))
  ), [ddexStores, selectedStoreIdSet]);

  const selectedOptionalStores = useMemo(() => ddexStores.filter((store) =>
    store.category === "optional" && selectedStoreIdSet.has(normalizeStoreId(store.id))
  ), [ddexStores, selectedStoreIdSet]);

  const selectedOptionalPaidStores = useMemo(() => ddexStores.filter((store) =>
    store.category === "optional_paid" && selectedStoreIdSet.has(normalizeStoreId(store.id))
  ), [ddexStores, selectedStoreIdSet]);

  const filteredMandatoryStores = useMemo(() => expandStoreEntries(selectedMandatoryStores.filter((store) =>
    getStoreLabel(store).toLowerCase().includes(storeSearchMatcher)
  )), [selectedMandatoryStores, storeSearchMatcher]);

  const filteredOptionalStores = useMemo(() => expandStoreEntries(selectedOptionalStores.filter((store) =>
    getStoreLabel(store).toLowerCase().includes(storeSearchMatcher)
  )), [selectedOptionalStores, storeSearchMatcher]);

  const filteredOptionalPaidStores = useMemo(() => expandStoreEntries(selectedOptionalPaidStores.filter((store) =>
    getStoreLabel(store).toLowerCase().includes(storeSearchMatcher)
  )), [selectedOptionalPaidStores, storeSearchMatcher]);

  return {
    selectedMandatoryStores,
    selectedOptionalStores,
    selectedOptionalPaidStores,
    filteredMandatoryStores,
    filteredOptionalStores,
    filteredOptionalPaidStores,
  };
};

export const useReleaseStores = ({
  ddexStores,
  storeSearchTerm,
  youtubeDeclarationE,
  setSelectedOptionalStores,
  setSelectedOptionalPaidStores,
  canSelectOptionalPaidStores = false,
  optionalPaidStoresDisabledReason,
  autoSelectPassingOptionalStores = true,
}) => {
  const { t } = useTranslation();
  const resolvedOptionalPaidStoresDisabledReason = optionalPaidStoresDisabledReason || t(
    "releases.store_picker.message.optional_paid_insufficient_tokens",
    {
      defaultValue: "Insufficient token balance. At least 1 token is required to select Optional Additional Stores.",
    },
  );

  const mandatoryStores = useMemo(() => ddexStores.filter((store) => {
    if (store.category !== "mandatory") {
      return false;
    }
    return true;
  }).map((store) => ({
    ...store,
    disabledReason: getStoreDisabledReason(store, t, youtubeDeclarationE),
  })), [ddexStores, t, youtubeDeclarationE]);

  const optionalStores = useMemo(() => ddexStores.filter((store) => {
    if (store.category !== "optional") {
      return false;
    }
    return true;
  }).map((store) => ({
    ...store,
    disabledReason: getStoreDisabledReason(store, t, youtubeDeclarationE),
  })), [ddexStores, t, youtubeDeclarationE]);

  const optionalPaidStores = useMemo(() => ddexStores
    .filter((store) => store.category === "optional_paid")
    .map((store) => {
      const apiDisabledReason = getStoreDisabledReason(store, t, youtubeDeclarationE);
      return {
        ...store,
        disabledReason: apiDisabledReason || (
          canSelectOptionalPaidStores ? null : resolvedOptionalPaidStoresDisabledReason
        ),
      };
    }), [ddexStores, canSelectOptionalPaidStores, resolvedOptionalPaidStoresDisabledReason, t, youtubeDeclarationE]);

  const selectedMandatoryStores = useMemo(() => mandatoryStores.filter((store) => !store.disabledReason), [mandatoryStores]);
  const selectableOptionalStores = useMemo(() => optionalStores.filter((store) => !store.disabledReason), [optionalStores]);
  const selectableOptionalPaidStores = useMemo(
    () => optionalPaidStores.filter((store) => !store.disabledReason),
    [optionalPaidStores],
  );
  const storeSearchMatcher = storeSearchTerm.trim().toLowerCase();

  const filteredMandatoryStores = useMemo(() => expandStoreEntries(mandatoryStores.filter((store) =>
    getStoreLabel(store).toLowerCase().includes(storeSearchMatcher)
  )), [mandatoryStores, storeSearchMatcher]);

  const filteredOptionalStores = useMemo(() => expandStoreEntries(optionalStores.filter((store) =>
    getStoreLabel(store).toLowerCase().includes(storeSearchMatcher)
  )), [optionalStores, storeSearchMatcher]);

  const filteredOptionalPaidStores = useMemo(() => expandStoreEntries(optionalPaidStores.filter((store) =>
    getStoreLabel(store).toLowerCase().includes(storeSearchMatcher)
  )), [optionalPaidStores, storeSearchMatcher]);

  useEffect(() => {
    if (!setSelectedOptionalStores) {
      return;
    }

    const availableOptionalIds = toNormalizedIdSet(selectableOptionalStores, (store) => store.id);

    setSelectedOptionalStores((prevStores) => {
      const filteredStores = prevStores.filter((store) =>
        availableOptionalIds.has(normalizeStoreId(store.id)),
      );

      let nextStores = filteredStores;
      if (autoSelectPassingOptionalStores) {
        const selectedIds = toNormalizedIdSet(filteredStores, (store) => store.id);
        const toAdd = selectableOptionalStores.filter((store) =>
          !selectedIds.has(normalizeStoreId(store.id)),
        );
        if (toAdd.length > 0) {
          nextStores = [...filteredStores, ...toAdd];
        }
      }

      if (
        nextStores.length === prevStores.length &&
        nextStores.every((store, index) => store.id === prevStores[index].id)
      ) {
        return prevStores;
      }

      return nextStores;
    });
  }, [autoSelectPassingOptionalStores, selectableOptionalStores, setSelectedOptionalStores]);

  useEffect(() => {
    if (!setSelectedOptionalPaidStores) {
      return;
    }

    const availableOptionalPaidIds = toNormalizedIdSet(
      selectableOptionalPaidStores,
      (store) => store.id,
    );

    setSelectedOptionalPaidStores((prevStores) => {
      const filteredStores = prevStores.filter((store) =>
        availableOptionalPaidIds.has(normalizeStoreId(store.id)),
      );

      let nextStores = filteredStores;
      if (autoSelectPassingOptionalStores) {
        const selectedIds = toNormalizedIdSet(filteredStores, (store) => store.id);
        const toAdd = selectableOptionalPaidStores.filter((store) =>
          !selectedIds.has(normalizeStoreId(store.id)),
        );
        if (toAdd.length > 0) {
          nextStores = [...filteredStores, ...toAdd];
        }
      }

      if (
        nextStores.length === prevStores.length &&
        nextStores.every((store, index) => store.id === prevStores[index].id)
      ) {
        return prevStores;
      }

      return nextStores;
    });
  }, [autoSelectPassingOptionalStores, selectableOptionalPaidStores, setSelectedOptionalPaidStores]);

  return {
    optionalStores,
    optionalPaidStores,
    selectableOptionalPaidStores,
    selectedMandatoryStores,
    filteredMandatoryStores,
    filteredOptionalStores,
    filteredOptionalPaidStores,
    showAvailabilityMessage: ddexStores.some((store) =>
      AVAILABILITY_TIP_REASON_CODES.has(store.reason_code)
    ),
  };
};

const ReleaseStores = ({
  storeSearchTerm,
  onStoreSearchChange,
  filteredMandatoryStores,
  filteredOptionalStores,
  filteredOptionalPaidStores,
  selectedMandatoryStores,
  selectedOptionalStores,
  selectedOptionalPaidStores = [],
  setSelectedOptionalStores,
  setSelectedOptionalPaidStores = noop,
  optionalPaidReadOnly = false,
  tokenOrderPath = "/tokens/order",
  loadingDdexStores,
  ddexStoresCount,
  showAvailabilityMessage,
  readOnly = false,
  hideSearch = false,
}) => {
  const { t } = useTranslation();
  const selectableOptionalPaidIds = toNormalizedIdSet(
    filteredOptionalPaidStores.filter((store) => !store.disabledReason),
    (store) => store.id,
  );
  const showOptionalAdditionalNoneSelectedWarning =
    !readOnly &&
    selectableOptionalPaidIds.size > 0 &&
    !selectedOptionalPaidStores.some((store) => selectableOptionalPaidIds.has(normalizeStoreId(store.id)));

  return (
    <Row style={{ marginBottom: "10px" }}>
      <Col>
        {!hideSearch && (
          <FormGroup>
            <Label>{t("releases.store_picker.label.stores")}</Label>
            <Input
              type="search"
              id="releaseStores"
              name="releaseStores"
              placeholder={t("releases.store_picker.placeholder.search")}
              value={storeSearchTerm}
              onChange={onStoreSearchChange}
            />
          </FormGroup>
        )}
        {hideSearch && <Label>{t("releases.store_picker.label.stores")}</Label>}
        <ReleaseStoreSection
          title={t("releases.store_picker.section.mandatory")}
          stores={filteredMandatoryStores}
          selectedStores={selectedMandatoryStores}
          setSelectedStores={noop}
          readOnly={true}
        />
        <ReleaseStoreSection
          title={t("releases.store_picker.section.optional")}
          stores={filteredOptionalStores}
          selectedStores={selectedOptionalStores}
          setSelectedStores={setSelectedOptionalStores}
          readOnly={readOnly}
        />
        <ReleaseStoreSection
          title={t("releases.store_picker.section.optional_locked")}
          stores={filteredOptionalPaidStores}
          selectedStores={selectedOptionalPaidStores}
          setSelectedStores={setSelectedOptionalPaidStores}
          readOnly={readOnly || optionalPaidReadOnly}
        />
        {showOptionalAdditionalNoneSelectedWarning && (
          <FormGroup>
            <FormText color="danger" style={{ fontWeight: 500 }}>
              {t("releases.store_picker.message.optional_additional_none_selected", {
                defaultValue: "You haven't selected any Optional Additional Stores. These can expand your reach, Are you sure you want to skip them?",
              })}
            </FormText>
          </FormGroup>
        )}
        {optionalPaidReadOnly && filteredOptionalPaidStores.length > 0 && (
          <FormText color="danger" style={{ marginBottom: "10px" }}>
            <Trans
              i18nKey="releases.store_picker.message.optional_paid_insufficient_tokens_with_link"
            >
              Your current token balance is insufficient. At least 1 token is required to select Optional Additional Stores.{" "}
              <a href={tokenOrderPath}>Order tokens</a> to continue.
            </Trans>
          </FormText>
        )}
        <FormGroup>
          {!loadingDdexStores && ddexStoresCount === 0 && (
            <FormText color="muted">
              {t("releases.store_picker.message.no_stores")}
            </FormText>
          )}
          {showAvailabilityMessage && (
            <FormText color="muted">
              {t("releases.store_picker.message.availability")}
            </FormText>
          )}
        </FormGroup>
      </Col>
    </Row>
  );
};

export default ReleaseStores;
